// Pure helpers for the rep "Request adjustment / Request void" feature
// (migration 0028, invoice_adjustment_requests). No React, no Supabase, no
// localStorage in this file so everything can be unit-tested.
//
// Nothing here writes anything. The Supabase-first writes live in
// src/supabase.js (createAdjustmentRequest / approveAdjustmentRequest ...).

import { round2, normalizePct, discountedTotal, rowDiscountPct, DISCOUNT_LABEL } from "./invoiceDiscount";
// Display only: the internal house-account stamp is shown as "MotorPower Care" in admin messages. Matching stays on the raw stamp.
import { displayRepStamp } from "./adminInvoicePure";
export { round2 };

// Invoice statuses a rep may request an ADJUSTMENT for. Rep-created invoices
// start as "pending" (rep form default) or "outstanding"; both mean unpaid.
// paid / payment_pending / cancelled are refused.
export const ADJUSTABLE_STATUSES = ["pending", "outstanding"];

const norm = (s) => String(s == null ? "" : s).trim().toLowerCase();

// portal_orders.items is a TEXT column holding JSON; the cache holds arrays.
export function parseItems(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim()) {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p : [];
    } catch (e) { return []; }
  }
  return [];
}

export function itemsSum(items) {
  return round2(parseItems(items).reduce((s, i) => s + (Number(i && i.price) || 0) * (Number(i && i.qty) || 0), 0));
}

// The stored ACCOUNT-DISCOUNT RATE of a rep field invoice (per-customer
// discount, migration 0034): inv.discount.pct on an INV-F / repCreated invoice,
// else 0. Only these invoices carry a pct that the adjust engine keeps: a rep
// invoice's discount is always "pct of the items sum", so a quantity change
// re-applies the same percent to the new subtotal. Invoices without a stored pct
// (every invoice created before the feature, and dealer-placed orders whose
// discount is not stored on the server) return 0 and keep the fixed-dollar
// behaviour below EXACTLY.
function accountPctFromInvoice(inv) {
  if (!inv || typeof inv.discount !== "object" || inv.discount === null) return 0;
  const fieldInvoice = !!inv.repCreated || String(inv.id || "").startsWith("INV-F");
  return fieldInvoice ? normalizePct(inv.discount.pct) : 0;
}

// The invoice's existing discount / shipping, read the same way the admin
// invoice modal reads them (inv.discount.amount / inv.shipping.amount, or a
// bare number). Legacy rep-created invoices have neither, so both are 0. A rep
// invoice with a stored account rate also returns discountPct (only then).
export function extrasFromInvoice(inv) {
  const num = (v) => {
    if (v == null) return 0;
    if (typeof v === "object") return Number(v.amount) || 0;
    return Number(v) || 0;
  };
  const out = { discount: round2(num(inv && inv.discount)), shipping: round2(num(inv && inv.shipping)) };
  const pct = accountPctFromInvoice(inv);
  if (pct > 0) out.discountPct = pct;
  return out;
}

// THE invoice total formula (same as AdminInvoiceModal's "expectedFromItems"
// and, for legacy rep-created invoices where discount = shipping = 0, the same
// as createInvoice's calcTotal): items sum minus discount plus shipping. With
// extras.discountPct (an account rate) the discount is re-derived from the NEW
// items sum: round2(sum * pct / 100), exactly what createInvoice stored.
export function recomputeTotal(items, extras) {
  const e = extras || {};
  const sum = itemsSum(items);
  const pct = normalizePct(e.discountPct);
  const discount = pct > 0 ? round2((sum * pct) / 100) : (Number(e.discount) || 0);
  const t = sum - discount + (Number(e.shipping) || 0);
  return round2(Math.max(0, t));
}

// Guard: the stored total of the ORIGINAL invoice must be explainable by the
// formula, otherwise a recomputed total would silently change something that
// is not the rep's edit. 2-cent tolerance (same as the admin modal).
export function totalFormulaConsistent(inv) {
  if (!inv) return false;
  const items = parseItems(inv.items);
  if (!items.length) return false;
  return Math.abs(recomputeTotal(items, extrasFromInvoice(inv)) - round2(inv.total)) <= 0.02;
}

// [{sku, qty}] as typed in the editor -> merged, cleaned lines. Blank SKU or
// qty < 1 lines are dropped; duplicate SKUs are merged (qty summed) so the
// rep cannot double-add one product on two lines.
export function normalizeLines(lines) {
  const order = [];
  const qty = {};
  (Array.isArray(lines) ? lines : []).forEach((l) => {
    const sku = String((l && l.sku) || "").trim();
    const q = Math.floor(Number(l && l.qty));
    if (!sku || !(q >= 1)) return;
    if (!(sku in qty)) { order.push(sku); qty[sku] = 0; }
    qty[sku] += q;
  });
  return order.map((sku) => ({ sku, qty: qty[sku] }));
}

// Build the PROPOSED item list. SKUs that already exist on the invoice keep
// their ORIGINAL item object (price, name, case size, variation info) and only
// take the new qty; new SKUs are priced by `lookup(sku)` (the same catalog
// price the rep sees when creating an invoice). Returns { items, missing }
// where `missing` lists new SKUs the catalog could not resolve.
export function buildProposedItems(originalItems, lines, lookup) {
  const orig = parseItems(originalItems);
  const bySku = {};
  orig.forEach((it) => { if (it && it.sku != null && !(String(it.sku) in bySku)) bySku[String(it.sku)] = it; });
  const items = [];
  const missing = [];
  normalizeLines(lines).forEach(({ sku, qty }) => {
    if (sku in bySku) { items.push({ ...bySku[sku], qty }); return; }
    const prod = typeof lookup === "function" ? lookup(sku) : null;
    if (!prod) { missing.push(sku); return; }
    const item = { sku, qty, name: prod.name || "", price: Number(prod.price) || 0, case: Number(prod.case) || 1 };
    if (prod.parentSku) item.parentSku = prod.parentSku;
    if (prod.variationLabel) item.variationLabel = prod.variationLabel;
    items.push(item);
  });
  return { items, missing };
}

// Line-by-line diff keyed by SKU (qty summed per SKU on each side).
// row: { sku, name, kind: added|removed|changed|unchanged, oldQty, newQty, oldPrice, newPrice }
export function diffItems(oldItems, newItems) {
  const collect = (items) => {
    const map = {};
    const order = [];
    parseItems(items).forEach((it) => {
      if (!it) return;
      const sku = String(it.sku != null ? it.sku : (it.name || ""));
      if (!(sku in map)) { map[sku] = { sku, name: it.name || "", qty: 0, price: Number(it.price) || 0 }; order.push(sku); }
      map[sku].qty += Number(it.qty) || 0;
    });
    return { map, order };
  };
  const a = collect(oldItems);
  const b = collect(newItems);
  const rows = [];
  const seen = new Set();
  a.order.forEach((sku) => {
    seen.add(sku);
    const o = a.map[sku];
    const n = b.map[sku];
    if (!n) rows.push({ sku, name: o.name, kind: "removed", oldQty: o.qty, newQty: 0, oldPrice: o.price, newPrice: 0 });
    else {
      const same = o.qty === n.qty && round2(o.price) === round2(n.price);
      rows.push({ sku, name: n.name || o.name, kind: same ? "unchanged" : "changed", oldQty: o.qty, newQty: n.qty, oldPrice: o.price, newPrice: n.price });
    }
  });
  b.order.forEach((sku) => {
    if (seen.has(sku)) return;
    const n = b.map[sku];
    rows.push({ sku, name: n.name, kind: "added", oldQty: 0, newQty: n.qty, oldPrice: 0, newPrice: n.price });
  });
  return rows;
}

export function sameItems(a, b) {
  return diffItems(a, b).every((r) => r.kind === "unchanged");
}

// True when the proposal differs from the invoice in at least one line or in
// payment terms. An identical proposal is pointless and is refused client-side.
export function hasAdjustmentChange(originalItems, proposedItems, originalTerms, proposedTerms) {
  if (!sameItems(originalItems, proposedItems)) return true;
  const ot = String(originalTerms || "").trim();
  const pt = String(proposedTerms == null ? originalTerms : proposedTerms || "").trim();
  return !!pt && pt !== ot;
}

// Validation of a proposal payload (used by the rep before submit and again by
// the admin approve helper against the FRESH request row).
// Returns null when valid, otherwise a short error string.
export function validateProposal(proposed, extras) {
  if (!proposed || typeof proposed !== "object") return "Missing proposed changes.";
  const items = proposed.items;
  if (!Array.isArray(items) || items.length < 1) return "An invoice must keep at least one line item. To cancel the whole invoice, request a void instead.";
  for (const it of items) {
    if (!it || !String(it.sku || "").trim()) return "Every line needs a product.";
    const q = Number(it.qty);
    if (!Number.isInteger(q) || q < 1) return `Quantity for ${it.sku} must be a whole number of at least 1.`;
    const p = Number(it.price);
    if (!Number.isFinite(p) || p < 0) return `Price for ${it.sku} is not valid.`;
  }
  const skus = items.map((i) => String(i.sku));
  if (new Set(skus).size !== skus.length) return "The same product appears on more than one line.";
  if (proposed.total != null) {
    if (Math.abs(recomputeTotal(items, extras) - round2(proposed.total)) > 0.005) return "The proposed total does not match the line items.";
  }
  return null;
}

// ── Eligibility ──────────────────────────────────────────────────────────────
// inv: the invoice as the rep sees it.
// ctx: { repId, repEmail, repName, namesMatch, inLocalList, pending }
//   repName + namesMatch: the rep's display name and the SAME fuzzy matcher
//     RepInvoices uses (namesMatchFuzzy in RepPortal.js). Invoices pulled from
//     portal_orders onto a second device / after a cache clear carry only the
//     rep NAME, never repId / repEmail, so the name is a legitimate ownership
//     signal. Falls back to a local copy of the matcher when not injected.
//   inLocalList: true when the invoice is in the rep's OWN invoices list
//   pending: the pending request (adjust or void) for this invoice, or null
// Returns { owned, canAdjust, canVoid, adjustReason, voidReason } where the
// *Reason strings are short tooltips explaining why an action is unavailable.
//
// "Rep-created" is the OLD rule (repCreated flag or INV-F id prefix). The
// cached `source` is deliberately NOT consulted: DB-hydrated invoices default a
// NULL source to "portal_order" in the cache, which is unknown, not evidence of
// a dealer order.
//
// VOID is limited to unpaid statuses (pending / outstanding / payment_pending),
// exactly as before this feature. A PAID invoice needs a refund, which only the
// admin Orders-tab void handles; it can never be voided through the rep queue.
export const VOIDABLE_STATUSES = ["pending", "outstanding", "payment_pending"];
export const PAID_VOID_TOOLTIP = "Paid invoices need a refund: ask admin to void it from the admin Orders tab.";

// Local copy of namesMatchFuzzy (RepPortal.js) so this module stays pure.
export function repNamesMatch(a, b) {
  const na = norm(a), nb = norm(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const clean = (s) => s.replace(/\./g, "").replace(/\s+/g, " ").trim();
  const ca = clean(na), cb = clean(nb);
  if (ca === cb) return true;
  const ta = ca.split(" "), tb = cb.split(" ");
  if (!ta[0] || ta[0] !== tb[0]) return false;
  if (ta.length === 1 || tb.length === 1) return true;
  const la = ta[ta.length - 1].charAt(0), lb = tb[tb.length - 1].charAt(0);
  return !!la && la === lb;
}

export function invoiceRequestEligibility(inv, ctx) {
  const c = ctx || {};
  const out = { owned: false, canAdjust: false, canVoid: false, adjustReason: "", voidReason: "" };
  if (!inv || !inv.id) return out;
  const repCreated = !!inv.repCreated || String(inv.id).startsWith("INV-F");
  if (!repCreated) {
    const why = "Only invoices you created can be adjusted or voided here. Dealer orders go through admin.";
    return { ...out, adjustReason: why, voidReason: why };
  }
  const match = typeof c.namesMatch === "function" ? c.namesMatch : repNamesMatch;
  const idMatch = !!(inv.repId && c.repId && String(inv.repId) === String(c.repId));
  const emailMatch = !!(inv.repEmail && c.repEmail && norm(inv.repEmail) === norm(c.repEmail));
  const noOwnerStamp = !inv.repId && !inv.repEmail;
  // A repId / repEmail stamp is authoritative: when the invoice carries one that
  // does not agree with this rep it VETOES the fuzzy name match (two reps with
  // colliding display names such as "Sam T."). The name match (and the
  // local-list fallback) apply only to invoices with no owner stamp at all.
  const nameMatch = noOwnerStamp && !!(inv.rep && c.repName && match(inv.rep, c.repName));
  const owned = idMatch || emailMatch || nameMatch || (noOwnerStamp && !!c.inLocalList);
  if (!owned) {
    const why = "You can only change invoices you created.";
    return { ...out, adjustReason: why, voidReason: why };
  }
  out.owned = true;
  const status = norm(inv.status);
  if (status === "cancelled") {
    const why = "This invoice is already cancelled.";
    return { ...out, adjustReason: why, voidReason: why };
  }
  if (c.pending) {
    const kind = c.pending.kind === "void" ? "void" : "adjustment";
    const why = `A ${kind} request is already waiting for admin review on this invoice.`;
    return { ...out, adjustReason: why, voidReason: why };
  }
  if (VOIDABLE_STATUSES.includes(status)) out.canVoid = true;
  else if (status === "paid" || status === "done") out.voidReason = PAID_VOID_TOOLTIP;
  else out.voidReason = "This invoice cannot be voided from here.";
  if (status === "paid" || status === "done") out.adjustReason = "Paid invoices cannot be adjusted. Ask admin to void it from the admin Orders tab (a refund is needed).";
  else if (status === "payment_pending") out.adjustReason = "A payment is awaiting admin confirmation. Wait for admin to confirm the payment before asking for a change.";
  else if (!ADJUSTABLE_STATUSES.includes(status)) out.adjustReason = "Only unpaid invoices can be adjusted.";
  else if (!totalFormulaConsistent(inv)) out.adjustReason = "This invoice total does not match its line items, so it cannot be adjusted automatically. Contact admin or request a void.";
  else out.canAdjust = true;
  return out;
}

// The single pending request (adjust or void) for an invoice, or null.
// adjustRows / voidRows are rows from invoice_adjustment_requests /
// invoice_void_requests. Returns { kind, row } or null.
export function pendingRequestFor(invoiceId, adjustRows, voidRows) {
  const a = (Array.isArray(adjustRows) ? adjustRows : []).find((r) => r && r.invoice_id === invoiceId && r.status === "pending");
  if (a) return { kind: "adjust", row: a };
  const v = (Array.isArray(voidRows) ? voidRows : []).find((r) => r && r.invoice_id === invoiceId && r.status === "pending");
  if (v) return { kind: "void", row: v };
  return null;
}

// ── Newest portal_orders row per invoice (shared rule) ───────────────────────
// The dedupe rule getAllPortalOrders has always used, extracted so approve picks
// the SAME row every screen shows. Rule (unchanged): key = invoice_ref ||
// invoice_id || order_ref || order_id || id; within a key the row with the
// strictly greatest created_at wins; on EQUAL created_at the row that comes
// FIRST in the input order wins (callers query created_at DESC).
export function orderRowKey(r) {
  return r.invoice_ref || r.invoice_id || r.order_ref || r.order_id || `__id_${r.id}`;
}
export function dedupeOrderRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  const map = {};
  rows.forEach((r) => {
    if (!r) return;
    const key = orderRowKey(r);
    const existing = map[key];
    if (!existing) { map[key] = r; return; }
    const newTs = new Date(r.created_at || 0).getTime();
    const oldTs = new Date(existing.created_at || 0).getTime();
    if (newTs > oldTs) map[key] = r;
  });
  return Object.values(map);
}
// The rows of ONE invoice, newest first, where element [0] is exactly the row
// dedupeOrderRows would keep. The rest follow by created_at desc (stable).
export function orderRowsNewestFirst(rows) {
  const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
  if (list.length < 2) return list;
  const newest = dedupeOrderRows(list)[0];
  const ts = (r) => { const t = new Date(r.created_at || 0).getTime(); return Number.isFinite(t) ? t : 0; };
  const rest = list.filter((r) => r !== newest).sort((a, b) => ts(b) - ts(a));
  return [newest, ...rest];
}

// Latest row per invoice_id (by created_at, ties by numeric id).
export function latestByInvoice(rows) {
  const map = {};
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r || !r.invoice_id) return;
    const cur = map[r.invoice_id];
    if (!cur) { map[r.invoice_id] = r; return; }
    const a = new Date(r.created_at || 0).getTime();
    const b = new Date(cur.created_at || 0).getTime();
    if (a > b || (a === b && Number(r.id || 0) > Number(cur.id || 0))) map[r.invoice_id] = r;
  });
  return map;
}

// ── Request state machine ────────────────────────────────────────────────────
// pending -> approved | rejected   (admin)
// pending -> cancelled             (the requesting rep)
// Everything else is refused; decided rows are terminal. (An approve that fails
// half-way leaves the request PENDING, so there is no approved -> pending path.)
export function canTransition(from, to, actor) {
  if (from === "pending" && (to === "approved" || to === "rejected")) return actor === "admin";
  if (from === "pending" && to === "cancelled") return actor === "rep";
  return false;
}

// Payment state is NOT stored in portal_orders, so no server-side guard is
// possible. Everything below works from what the admin device knows.
// unpaid = pending / outstanding / blank; the other buckets are distinct.
export function statusClass(s) {
  const v = norm(s);
  if (v === "paid" || v === "done") return "paid";
  if (v === "payment_pending") return "payment_pending";
  if (v === "cancelled") return "cancelled";
  return "unpaid";
}

const isFieldRef = (ref) => String(ref || "").startsWith("INV-F");

// Checks before an approve may touch anything.
//   orderRows      every portal_orders row for the invoice_ref, NEWEST FIRST
//   cacheFound     the invoice exists in the admin device's cache (required)
//   cacheStatus    the admin cache's status for it
//   snapshotStatus the status the rep's device showed when filing
// The admin cache is the only payment signal available; this is a mitigation,
// not a guarantee (another device may hold a newer payment).
export function approvalBlockReason({ request, orderRows, cacheStatus, cacheFound, snapshotStatus }) {
  // FAIL CLOSED: a caller that forgets the payment-signal inputs must not get an
  // implicit "allow". snapshotStatus may be null / "" (the rep's device did not
  // know a status) but it must be SUPPLIED; cacheStatus is required whenever
  // the invoice was found in the admin cache.
  if (typeof cacheFound !== "boolean" || snapshotStatus === undefined || (cacheFound && cacheStatus === undefined)) {
    throw new TypeError("approvalBlockReason requires cacheFound (boolean), cacheStatus (when found) and snapshotStatus.");
  }
  if (!request) return "Request not found.";
  if (request.status !== "pending") return `This request is already ${request.status}.`;
  const rows = Array.isArray(orderRows) ? orderRows : [];
  if (!rows.length) return "No order for this invoice exists in the database yet (it may still be syncing from the rep's device). Nothing was changed.";
  const newest = rows[0];
  if (norm(newest.status) === "cancelled") return "This invoice is already cancelled. Nothing was changed.";
  // A NULL / "portal_order" source is not evidence of a dealer order for an
  // INV-F rep invoice (savePortalOrder's fallback defaults the source).
  if (newest.source === "admin_invoice") return "This invoice was created in the office and cannot be adjusted through a rep request.";
  if (newest.source === "portal_order" && !isFieldRef(newest.invoice_ref || request.invoice_id)) return "This is a dealer-placed order and cannot be adjusted through a rep request.";
  if (!cacheFound) return "This invoice is not in this device's invoice list, so its payment status cannot be checked. Reload the admin portal and try again.";
  const cs = statusClass(cacheStatus);
  if (cs !== "unpaid") return "This invoice is marked paid, payment-pending or cancelled on this device. An adjustment would change what the dealer already paid. Reject this request and ask the rep to request a void instead.";
  if (snapshotStatus != null && snapshotStatus !== "" && statusClass(snapshotStatus) !== cs) return "The invoice status on this device differs from the status the rep saw when filing the request. Reject it and ask the rep to file a new one.";
  return null;
}

// Ownership: the portal_orders row must belong to the rep who filed. The table
// has a rep NAME column (and possibly rep id / email columns); match whatever
// exists. `match` is a fuzzy name matcher. Returns null when fine, else a message.
export function requestOwnershipProblem(request, row, match) {
  if (!request || !row) return "Request or order row missing.";
  const m = typeof match === "function" ? match : repNamesMatch;
  if (row.rep_id && request.rep_id && String(row.rep_id) !== String(request.rep_id)) return `Order row #${row.id} belongs to a different rep than the one who filed this request. Reject it.`;
  if (row.rep_email && request.rep_email && norm(row.rep_email) !== norm(request.rep_email)) return `Order row #${row.id} belongs to a different rep (email) than the one who filed this request. Reject it.`;
  if (!norm(row.rep)) return `Order row #${row.id} has no rep name, so its owner cannot be verified. Reject this request.`;
  if (!m(row.rep, request.rep_name)) return `Order row #${row.id} is credited to "${displayRepStamp(row.rep)}", but the request was filed by "${request.rep_name || "unknown"}". Reject this request.`;
  return null;
}

// Baseline check for ONE row: the rep captured the DB row's items + total at
// request time. Only the NEWEST row (the one getAllPortalOrders exposes) is
// checked; older duplicate rows never block an approval.
export function rowBaselineMismatch(snapshotDb, row) {
  if (!snapshotDb || typeof snapshotDb !== "object" || !row) return false; // no baseline: cannot compare
  return !sameItems(parseItems(snapshotDb.items), parseItems(row.items)) || Math.abs(round2(row.total) - round2(snapshotDb.total)) > 0.005;
}
export function baselineMismatch(snapshotDb, orderRows) {
  const rows = Array.isArray(orderRows) ? orderRows : [];
  return rows.length ? rowBaselineMismatch(snapshotDb, rows[0]) : false;
}

// The field half of the ONE "apply in progress" predicate that the guard trigger
// in migration 0028 enforces (the trigger additionally sees whether ANY
// portal_orders row carries the request id): the admin has written the audit
// (pre_apply_order) and no conclusive failure has been recorded since
// (last_apply_error is null). While true, reject / rep-cancel are refused.
export function applyInProgressByFields(req) {
  return !!req && req.pre_apply_order != null && req.last_apply_error == null;
}

// True when a row already carries THIS request's marker and the proposed
// lines/total: an idempotent retry must skip it.
export function rowAlreadyApplied(row, requestId, proposedItems, proposedTotal) {
  if (!row || row.adjustment_request_id == null || String(row.adjustment_request_id) !== String(requestId)) return false;
  return sameItems(parseItems(row.items), proposedItems) && Math.abs(round2(row.total) - round2(proposedTotal)) <= 0.011;
}

// The cache half of an adjusted DISCOUNTED invoice: when the DB row stores an
// account rate (discount_pct), the cached subtotal and discount.amount follow the
// new lines (amount = round2(itemsSum * pct / 100), the same rule that wrote the
// row's total), so the cached invoice keeps satisfying totalFormulaConsistent and
// the invoice modals show the new breakdown. {} for a row without a stored rate
// (legacy behaviour: nothing about a discount is touched).
function accountDiscountPatch(cached, row, items) {
  const pct = rowDiscountPct(row);
  if (pct <= 0) return {};
  const subtotal = itemsSum(items);
  const prevLabel = cached && cached.discount && typeof cached.discount === "object" ? cached.discount.label : "";
  return { subtotal, discount: { pct, amount: round2((subtotal * pct) / 100), label: prevLabel || DISCOUNT_LABEL } };
}

// Cache reconciliation (SHARED_INVOICES.applyAdjustment) ─────────────────────
// Idempotent. `cached` is the invoice in a browser cache, `row` a portal_orders
// row. Applies items / total / subtotal / payment terms ONLY when the DB row
// carries an adjustment marker that the cache does not (forward, guarded by
// adjusted_at so an older adjustment never replaces a newer one), or when the
// cache carries a marker the DB row no longer has (revert). Never for a cached
// invoice that is paid / payment_pending / cancelled. Never touches shipping,
// notes, status or any paid field. Returns a patch object or null.
// allowRevert must be passed as true ONLY by callers whose row is known to be the
// NEWEST row of its invoice_ref (results of getAllPortalOrders). A single row
// from a realtime event may be an older duplicate without a marker and must
// never revert the cache.
// opts.fetchStartedAt (ms since epoch): when the batch fetch began. A revert is
// skipped when the cached adjustment is NEWER than that (the fetch may have read
// the row before the approve wrote it), so a slow in-flight fetch can never
// revert a just-applied approve.
export function adjustmentPatch(cached, row, opts) {
  if (!cached || !row) return null;
  const allowRevert = !!(opts && opts.allowRevert === true);
  const marker = row.adjustment_request_id;
  const st = norm(cached.status);
  if (st === "cancelled" || st === "done") return null;
  if (st === "paid" || st === "payment_pending") {
    // Only the REP's own device may opt in (opts.allowPaidPatch), and only for a
    // rep-created INV-F invoice and only FORWARD (an unapplied marker on the DB
    // row). The dealer cache and the admin cache never pass this option, so for
    // them a paid cache still refuses. Status / paid fields are never touched.
    if (!(opts && opts.allowPaidPatch === true)) return null;
    const ref = String(row.invoice_ref || row.invoice_id || cached.id || "");
    if (!ref.startsWith("INV-F")) return null;
    if (marker == null || marker === "") return null;
  }
  const cachedMarker = String(cached.adjustmentRequestId || "");
  const rowMarker = marker == null || marker === "" ? "" : String(marker);
  const items = parseItems(row.items);
  const total = Number(row.total);
  // REVERT: the cache carries an adjustment the database no longer has (an
  // approve was undone / restored by a developer). The DB row wins.
  if (!rowMarker) {
    if (!allowRevert || !cachedMarker) return null;
    const startedAt = opts ? Number(opts.fetchStartedAt) : NaN;
    if (Number.isFinite(startedAt) && cached.adjustedAt) {
      const c = new Date(cached.adjustedAt).getTime();
      if (!isNaN(c) && c > startedAt) return null;
    }
    if (!items.length || !Number.isFinite(total)) return null;
    const patch = { items, total: round2(total), adjustmentRequestId: "", adjustedAt: "" };
    if (cached.subtotal != null) patch.subtotal = itemsSum(items);
    Object.assign(patch, accountDiscountPatch(cached, row, items));
    if (row.payment_terms && row.payment_terms !== cached.paymentTerms) patch.paymentTerms = row.payment_terms;
    return patch;
  }
  if (cachedMarker === rowMarker) return null;
  if (!items.length) return null;
  if (!Number.isFinite(total)) return null;
  // Forward-only: never replace a NEWER adjustment with an older one.
  if (cached.adjustedAt && row.adjusted_at) {
    const c = new Date(cached.adjustedAt).getTime();
    const r = new Date(row.adjusted_at).getTime();
    if (!isNaN(c) && !isNaN(r) && r < c) return null;
  }
  const patch = { items, total: round2(total), adjustmentRequestId: rowMarker };
  if (cached.subtotal != null) patch.subtotal = itemsSum(items);
  Object.assign(patch, accountDiscountPatch(cached, row, items));
  if (row.payment_terms && row.payment_terms !== cached.paymentTerms) patch.paymentTerms = row.payment_terms;
  if (row.adjusted_at) patch.adjustedAt = row.adjusted_at;
  return patch;
}

// Compare-and-set expectation used when applying an approved adjustment:
// true when the row still holds exactly the items text + total that were read.
export function rowMatchesRead(current, read) {
  if (!current || !read) return false;
  return String(current.items == null ? "" : current.items) === String(read.items == null ? "" : read.items)
    && Math.abs(round2(current.total) - round2(read.total)) < 0.0001;
}

// Short label + colours for the rep's status pill (list row + invoice modal).
// adjustReq / voidReq are the LATEST request of each kind for the invoice.
export function requestPill(adjustReq, voidReq) {
  const a = adjustReq && adjustReq.status;
  const v = voidReq && voidReq.status;
  // An ADMIN-initiated adjustment (migration 0032) is never something the rep asked for:
  // its wording must not say "request" / "approved".
  const adminInit = (r) => !!r && r.initiated_by === "admin";
  if (a === "pending") return adminInit(adjustReq)
    ? { key: "adj-admin-pending", label: "Admin adjustment in progress", tone: "amber" }
    : { key: "adj-pending", label: "Adjustment pending", tone: "amber" };
  if (v === "pending") return { key: "void-pending", label: "Void pending", tone: "amber" };
  const ts = (r) => (r ? new Date(r.decided_at || r.updated_at || r.created_at || 0).getTime() : 0);
  const cand = [];
  // A rejected admin-initiated row is an admin-side clean-up, not a decision on a rep request: not shown to the rep.
  if (adjustReq && (a === "approved" || (a === "rejected" && !adminInit(adjustReq)))) cand.push({ t: ts(adjustReq), kind: "adj", s: a, note: adminInit(adjustReq) ? (adjustReq.reason || "") : (adjustReq.decision_note || ""), admin: adminInit(adjustReq) });
  if (voidReq && (v === "approved" || v === "rejected")) cand.push({ t: ts(voidReq), kind: "void", s: v, note: voidReq.decision_note || "" });
  if (!cand.length) return null;
  cand.sort((x, y) => y.t - x.t);
  const top = cand[0];
  if (top.kind === "adj") return top.s === "approved"
    ? (top.admin
      ? { key: "adj-admin", label: "Adjusted by admin", tone: "green", note: top.note }
      : { key: "adj-approved", label: "Adjustment approved", tone: "green", note: top.note })
    : { key: "adj-rejected", label: "Adjustment rejected", tone: "gray", note: top.note };
  // A void request the admin "approved" only to dismiss it because the invoice was ALREADY cancelled
  // (the admin voided it on their own, decision_note carries the already-voided sentence) was not
  // granted on the rep's request: say so.
  if (top.s === "approved" && /already voided/i.test(String(top.note || ""))) {
    return { key: "void-already", label: "Already voided by admin", tone: "red", note: top.note };
  }
  return top.s === "approved"
    ? { key: "void-approved", label: "Void approved", tone: "red", note: top.note }
    : { key: "void-rejected", label: "Void rejected", tone: "gray", note: top.note };
}

// New total for an approved adjustment, derived from the LIVE database row so
// a rep-supplied total or discount/shipping can never be trusted: the amount
// that the stored total differs from its own items sum (i.e. minus discount
// plus shipping, 0 for rep-created field invoices) is carried over unchanged.
//
// A row that stores an account rate (portal_orders.discount_pct, migration 0034)
// passes it as discountPct: the new total is then the SAME function the client
// used (discountedTotal of the new items sum), never a carried dollar delta. A
// row without a stored rate (every older invoice) keeps the delta rule above.
export function expectedNewTotal(oldItems, oldTotal, newItems, discountPct) {
  const pct = normalizePct(discountPct);
  if (pct > 0) return discountedTotal(itemsSum(newItems), pct);
  const delta = round2(oldTotal) - itemsSum(oldItems);
  return round2(Math.max(0, itemsSum(newItems) + delta));
}
